import type { Nda, NdaAppliesTo, NdaRestrictionType } from "../types/nda";
import { isNdaAppliesTo } from "../types/nda";

export type ExtractionNdaDraft = {
  restriction_type: NdaRestrictionType;
  expiry_date: string;
  applies_to: NdaAppliesTo | null;
  summary: string | null;
  person_responsible_id: number | null;
};

/** Prefer top-level `nda_data`; ignore legacy nested `deal_data.nda`. */
export function pickExtractionNdaData(
  extraction: {
    nda_data?: unknown;
    deal_data?: unknown;
  },
): Nda | null {
  const top = extraction.nda_data;
  if (top && typeof top === "object" && !Array.isArray(top)) {
    return normalizeExtractionNdaDraft(top as Record<string, unknown>);
  }
  return null;
}

export function normalizeExtractionNdaDraft(
  raw: Record<string, unknown> | null | undefined,
): Nda | null {
  if (!raw || typeof raw !== "object") return null;

  const type = raw.restriction_type;
  const restriction_type: NdaRestrictionType =
    type === "no_contact" || type === "non_circumvent"
      ? type
      : "non_circumvent";

  const expiryRaw = raw.expiry_date;
  const expiry_date =
    typeof expiryRaw === "string" && expiryRaw.trim()
      ? expiryRaw.trim().slice(0, 10)
      : null;

  const applies = raw.applies_to;
  const applies_to =
    typeof applies === "string" && isNdaAppliesTo(applies) ? applies : null;

  const summary =
    typeof raw.summary === "string" && raw.summary.trim()
      ? raw.summary.trim()
      : null;

  const person =
    typeof raw.person_responsible_id === "number"
      ? raw.person_responsible_id
      : raw.person_responsible &&
          typeof raw.person_responsible === "object" &&
          typeof (raw.person_responsible as { id?: unknown }).id === "number"
        ? ((raw.person_responsible as { id: number }).id)
        : null;

  return {
    restriction_type,
    expiry_date,
    applies_to,
    summary,
    person_responsible_id: person,
  };
}

export function toExtractionNdaDraftForm(
  nda: Nda | null | undefined,
): ExtractionNdaDraft | null {
  if (!nda) return null;
  const type = nda.restriction_type;
  return {
    restriction_type:
      type === "no_contact" || type === "non_circumvent"
        ? type
        : "non_circumvent",
    expiry_date: nda.expiry_date ? String(nda.expiry_date).slice(0, 10) : "",
    applies_to: isNdaAppliesTo(nda.applies_to) ? nda.applies_to : null,
    summary: nda.summary ?? null,
    person_responsible_id:
      typeof nda.person_responsible_id === "number"
        ? nda.person_responsible_id
        : null,
  };
}

/** Strip legacy nested NDA from deal payloads used for merge / POST deals. */
export function stripLegacyDealNda<T extends Record<string, unknown>>(
  dealData: T,
): T {
  const next = { ...dealData };
  delete next.nda;
  delete next.nda_id;
  return next;
}
